import React from 'react';
import PropTypes from 'prop-types';
import MultiSelectBox from '../../Common/MultiSelectBox/MultiSelectBox';
import Toggle from '../../Common/Toggle/Toggle';
import '../Reports.scss';
import { getMilestoneIds } from '../../../helpers/reports';

export class GMSummaryFilters extends React.Component {

    constructor(props, context) {
        super(props, context);

        const initialFormSelections = {
            locationKeys: '',
            communityIds: '',
            milestoneIds: '',
            highLevelOnly: false,
            breakdownBy: ['Discipline']
        }

        this.state = {
            emptyForm: initialFormSelections,
            locationKeys: initialFormSelections.locationKeys,
            communityIds: initialFormSelections.communityIds,
            milestoneIds: initialFormSelections.milestoneIds,
            highLevelOnly: initialFormSelections.highLevelOnly,
            breakdownBy: initialFormSelections.breakdownBy,
            breakdowns: [
                { id: 'Discipline', name: 'Discipline' },
                { id: 'Department', name: 'Department' },
                { id: 'Phase', name: 'Phase' },
                { id: 'Responsible', name: 'Responsible' },
                { id: 'Community', name: 'Community' }
            ]
        }
    }

    componentDidMount = async () => {
        /**
         * Must set defaults before registering report filter functions, because
         * these defaults might be overridden by mapToForm values coming from
         * the API when opening a scheduled report.
         */
        this.setDefaults();
        /**
         * On mount, register all of this form's callbacks so the parent is able
         * to use them when its wrapping UI indicates these actions should be
         * performed.
         */
        this.props.registerReportFilterFunctions(
            this.props.report.id,
            this.clearForm,
            this.validateForm,
            this.mapToForm,
            this.mapToApiRequest
        );
    }

    setDefaults = () => {
        let newState = {...this.state};

        const appState = this.props.savedFilterValues;
        if (appState) {
            // Set new state to values from redux app state
            newState = {
                ...newState,
                locationKeys: appState.locationKeys ? appState.locationKeys : newState.locationKeys,
                communityIds: appState.communityIds ? appState.communityIds : newState.communityIds,
                milestoneIds: appState.milestoneIds ? appState.milestoneIds : newState.milestoneIds,
                highLevelOnly: appState.highLevelOnly ? appState.highLevelOnly : newState.highLevelOnly,
                breakdownBy: appState.breakdownBy ? appState.breakdownBy : newState.breakdownBy
            }
        }

        // Possibly override app state values with default values or URL params
        let locationKeys = this.getDefaultLocationKeys(newState.locationKeys);
        let milestoneIds = this.getDefaultMilestone(newState.milestoneIds);

        newState = {
            ...newState,
            locationKeys: locationKeys,
            milestoneIds : milestoneIds
        }

        this.setState({
            ...this.state,
            ...newState
        });

    }

    getDefaultLocationKeys = (savedLocationKeys) => {
        /**
         * Location(s) can come from three places:
         *  1. The URL params
         *  2. Already selected Location Keys saved in app state
         *  3. The current user's default location
         *
         * When this report is called from the Task List page of the site, there is an attempt
         * to pass along any location(s) the user has already selected on that page, to auto-populate
         * the location field here.  We try to grab those values first.
         *
         * If the URL params location(s) aren't set, then we fallback to the selected location keys,
         * and if none have been selected, then we try to pull the default location from the current
         * user's default location.
         */

        
        if (Object.keys(this.props.urlParams).length > 0) {
            const urlLocationKeys = this.props.urlParams.locationKeys;
            if (urlLocationKeys && urlLocationKeys.length > 0) {
                return urlLocationKeys.join(',');
            }
            
            return '';
        }

        if (savedLocationKeys) {
            return savedLocationKeys;
        }
        
        const defaultLocationKey = this.props.filters.defaultLocationKey;

        if (defaultLocationKey && defaultLocationKey.trim().length > 0) {
            return defaultLocationKey;
        }

        return '';
    }

    getDefaultMilestone = (savedMilestoneIds) => {
        /**
         * The isMilestone URL param optionally sets one of the values in the
         * hard-coded milestone options list.  0 = Non-milestones, 1 = Milestones.
         * If no URL params have been set, we fallback to any saved milestone Ids
         * that were previously selected.
         */
        
        if (Object.keys(this.props.urlParams).length > 0) {

            const urlIsMilestone = this.props.urlParams.isMilestone;
            if (urlIsMilestone && urlIsMilestone.trim().length > 0) {
                return urlIsMilestone;
            }
            
            return '';
        }

        if (savedMilestoneIds) {
            return savedMilestoneIds;
        }

        return '';
    }

    clearForm = () => {
        this.setState({
            ...this.state,
            ...this.state.emptyForm
        });
    }

    validateForm = () => {
        return true;
    }

    mapToForm = (reportRequest) => {
        const form = { ...this.state.emptyForm };

        form.locationKeys = (reportRequest.locationKeys) ? reportRequest.locationKeys.join(',') : form.locationKeys;
        form.communityIds = (reportRequest.communityIds) ? reportRequest.communityIds.join(',') : form.communityIds;
        form.highLevelOnly = (reportRequest.highLevelOnly === true) ? true : false;
        form.breakdownBy = (reportRequest.breakdownBy) ? reportRequest.breakdownBy : form.breakdownBy;
        form.milestoneIds = getMilestoneIds(reportRequest.isMilestone);

        this.setState({
            ...this.state,
            ...form
        });
    }

    mapToApiRequest = () => {
        let isMilestone = null; // default to none or both
        if (this.state.milestoneIds.length > 0) {
            const selectedMilestoneIds = this.state.milestoneIds.split(',');
            if (selectedMilestoneIds.length == 1) {
                isMilestone = selectedMilestoneIds[0] === '1' ? true : false;
            }
        }

        const request = {
            reportId: this.props.report.id,
            locationKeys: this.state.locationKeys.split(','),
            communityIds: this.state.communityIds.split(','),
            isMilestone: isMilestone,
            highLevelOnly: this.state.highLevelOnly,
            breakdownBy: this.state.breakdownBy.toString()
        };

        return {
            apiRequest: request,
            formValues: this.state
        };
    }

    onFieldChange = (name, value) => {
        this.setState({ ...this.state, [name]: value });
    }

    onDropdownChange = (value, name) => {
        this.onFieldChange(name, value);
    }

    render() {
        return (
            <form id="subForm" noValidate className={this.props.isFormValid ? "" : "was-validated"}>
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="locationKeys"
                            label="Locations" idField="id" valueField="name"
                            options={this.props.filters.locations || []}
                            selectedValues={this.state.locationKeys.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            errorMessage="Location is required"
                            isMulti={true} />            
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="communityIds"
                            label="Communities" idField="id" valueField="name"
                            options={this.props.filters.communities || []}
                            selectedValues={this.state.communityIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            errorMessage="Community is required"
                            isMulti={true} />            
                    </div>
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="breakdownBy"
                            label="Breakdown By" idField="id" valueField="name"
                            options={this.state.breakdowns || []}
                            selectedValues={this.state.breakdownBy.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={false} />            
                    </div>
                </div>

                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <MultiSelectBox name="milestoneIds"
                            label="Milestone" idField="id" valueField="name"
                            options={this.props.filters.milestones}
                            selectedValues={this.state.milestoneIds.toString()}
                            onChange={this.onDropdownChange}
                            isRequired={false}
                            isMulti={true} />
                    </div>
                </div>
                
                <div className="row">
                    <div className="col-sm-12 col-md-4">
                        <Toggle name="highLevelOnly"
                            text="High Level Only"
                            checked={this.state.highLevelOnly}
                            onChange={this.onFieldChange} />
                    </div>
                </div>                
            </form>
        );
    }
}

GMSummaryFilters.propTypes = {
    report: PropTypes.object.isRequired,
    filters: PropTypes.object.isRequired,
    isFormValid: PropTypes.bool.isRequired,
    registerReportFilterFunctions: PropTypes.func.isRequired,
    savedFilterValues: PropTypes.object.isRequired,
    urlParams: PropTypes.object.isRequired,
};

export default GMSummaryFilters;
